/**
 * AuthRegistry: Domain-centric class for authentication state and logic.
 * All state and logic for authentication is encapsulated here.
 * Use getAuthRegistry() for singleton access.
 * 
 * Session persistence: Tokens and user are stored in localStorage to
 * maintain login across page refreshes. Session lasts until JWT expires.
 */

import type { User } from "../types";

export interface AuthTokens {
  accessToken: string | null;
  refreshToken: string | null;
}

const REGION_STORAGE_KEY = "boona:region";
const TOKEN_STORAGE_KEY = "boona:access_token";
const USER_STORAGE_KEY = "boona:user";

/**
 * Safe localStorage wrapper with error handling.
 * Shows user-facing error if localStorage is not available.
 */
function safeStorageGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.error("[AuthRegistry] localStorage read failed:", e);
    return null;
  }
}

function safeStorageSet(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    localStorage.setItem(key, value);
    // Verify the value was actually stored
    const stored = localStorage.getItem(key);
    if (stored !== value) {
      throw new Error("Value verification failed");
    }
    return true;
  } catch (e) {
    console.error("[AuthRegistry] localStorage write failed:", e);
    showStorageError();
    return false;
  }
}

function safeStorageRemove(key: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error("[AuthRegistry] localStorage remove failed:", e);
  }
}

let storageErrorShown = false;

function showStorageError(): void {
  if (storageErrorShown) return;
  storageErrorShown = true;

  // Show user-facing error
  const message =
    "Your browser does not support localStorage or it is disabled.\n\n" +
    "This may happen in:\n" +
    "• Private/Incognito browsing mode\n" +
    "• Browsers with strict privacy settings\n" +
    "• When cookies are fully blocked\n\n" +
    "Please enable localStorage or switch to a normal browsing window.";

  // Use alert for immediate visibility, or could dispatch a custom event
  // that a React component listens to for a nicer UI
  if (typeof window !== "undefined" && window.alert) {
    window.alert(message);
  }
}

export class AuthRegistry {
  // Initialize tokens from localStorage for session persistence
  private tokens: AuthTokens = (() => {
    const stored = safeStorageGet(TOKEN_STORAGE_KEY);
    return stored ? { accessToken: stored, refreshToken: null } : { accessToken: null, refreshToken: null };
  })();

  // Initialize user from localStorage for session persistence
  private user: User | null = (() => {
    try {
      const stored = safeStorageGet(USER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();

  private region: string | null = safeStorageGet(REGION_STORAGE_KEY);

  // Read-only accessors
  getUser(): User | null {
    return this.user;
  }

  getTokens(): AuthTokens {
    return { ...this.tokens };
  }

  getRegion(): string | null {
    return this.region;
  }

  isAuthenticated(): boolean {
    return !!this.user && !!this.tokens.accessToken;
  }

  /**
   * Check if the stored access token is still valid (not expired).
   * Returns false if no token exists or if it's expired.
   */
  isTokenValid(): boolean {
    const token = this.tokens.accessToken;
    if (!token) return false;
    try {
      const parts = token.split(".");
      if (parts.length < 2) return false;
      // Decode base64url payload
      const payload = parts[1];
      const padded = payload.padEnd(payload.length + (4 - (payload.length % 4)) % 4, "=");
      const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
      const decoded = JSON.parse(json);
      // Check expiration (exp is in seconds, Date.now() is in ms)
      return decoded.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  // Controlled mutation methods
  setAuth(user: User, tokens: AuthTokens, region?: string | null) {
    this.user = user;
    this.tokens = { ...tokens };

    // Persist to localStorage for session persistence
    if (tokens.accessToken) {
      safeStorageSet(TOKEN_STORAGE_KEY, tokens.accessToken);
    }
    safeStorageSet(USER_STORAGE_KEY, JSON.stringify(user));

    if (region) {
      this.region = region;
      safeStorageSet(REGION_STORAGE_KEY, region);
    }
  }

  clearAuth() {
    this.user = null;
    this.tokens = { accessToken: null, refreshToken: null };
    this.region = null;
    safeStorageRemove(REGION_STORAGE_KEY);
    safeStorageRemove(TOKEN_STORAGE_KEY);
    safeStorageRemove(USER_STORAGE_KEY);
  }
}

// Singleton accessor
let _authRegistry: AuthRegistry | null = null;

export function getAuthRegistry(): AuthRegistry {
  if (!_authRegistry) {
    _authRegistry = new AuthRegistry();
  }
  return _authRegistry;
}
